<template>
  <v-card>
    <v-card-title class="toolbar white--text">
      <h3>Emoney Users ({{ filteredItems.length }}/{{ emoneyUsersPagination.total }})</h3>
      <v-spacer />
      <div class="ml-5">
        <v-select
          v-model="searchColumn"
          class="ml-5"
          primary
          dark
          :items="searchColumnOptions"
          label="Search by"
          item-text="title"
          item-value="value"
          clearable
          @change="onChangePagination"
        />
      </div>
      <v-text-field
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        label="Search term"
        single-line
        hide-details
      />
    </v-card-title>
    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        Filtered results ({{ filteredResults.length }}/{{ filteredItems.length }})
        <v-tooltip
          v-if="!hasResultFilters"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon>
                filter_list
              </v-icon>
            </v-btn>
          </template>
          <span>Filters</span>
        </v-tooltip>
        <v-tooltip
          v-else
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="primary"
              v-on="on"
              @click="resetResultsFilters"
            >
              <v-icon>
                restore
              </v-icon>
            </v-btn>
          </template>
          <span>Reset filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="resultFilters.kycStatus"
        class="ml-5"
        primary
        :items="kycStatus"
        label="Kyc status"
        item-text="title"
        item-value="status"
        clearable
      />

      <v-text-field
        v-model="resultFilters.searchResults"

        class="ml-5"
        append-icon="search"
        label="filter results"
      />
    </v-card-title>
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredResults"
      :pagination.sync="pagination"
      :total-items.sync="emoneyUsersPagination.total"
      :rows-per-page-items="rowsPerPageItems"
      :loading="loading"
      :expand="expand"
      item-key="id"
      class="elevation-1"
      @update:pagination="updatePagination"
    >
      <template v-slot:items="props">
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="[
              header.sortable ? 'column sortable' : '',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : ''
            ]"
            @click="header.sortable && changeSort(header.value)"
          >
            <v-icon
              v-if="header.sortable"
              small
            >
              arrow_upward
            </v-icon>
            {{ header.text }}
          </th>
        </tr>
      </template>
      <template v-slot:items="props">
        <tr
          :active="props.selected"
          @click="expandRow(props); props.expanded = !props.expanded"
        >
          <td class="text-xs-left">
            <v-layout>
              <v-tooltip
                top
                dark
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    @click="lockUserAccount(
                      {
                        userId: props.item.ext_user_id,
                        locked: props.item.locked_at ? false : true
                      }
                    )"
                    v-on="on"
                  >
                    <v-icon color="primary">
                      {{ props.item.locked_at ? 'lock' : 'lock_open' }}
                    </v-icon>
                  </v-btn>
                </template>
                User is:
                {{ props.item.locked_at
                  ? `LOCKED AT: ${ formatDate(props.item.locked_at)}` : 'UNLOCKED'
                }}
              </v-tooltip>
              <UserChip
                :id="props.item.ext_user_id"
                :key="`user-chip-${props.item.ext_user_id}`"
              />
              <v-tooltip
                top
                dark
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    :href="`/#/ident-cases?userId=${props.item.ext_user_id}`"
                    v-on="on"
                  >
                    <v-icon color="primary">
                      fingerprint
                    </v-icon>
                  </v-btn>
                </template>
                View user ident cases
              </v-tooltip>
            </v-layout>
          </td>
          <td class="text-xs-center">
            <v-chip
              v-if="props.item.mangopay_user_id"
              color="secondary"
              outline
              small
            >
              {{ props.item.mangopay_user_id }}
            </v-chip>
          </td>
          <td class="text-xs-center">
            {{ props.item.kyc_doc_id }}
          </td>
          <td class="text-xs-center">
            <v-chip
              v-if="props.item.kyc_doc_status"
              :color="getStatusColor(props.item)"
              outline
              small
            >
              {{ props.item.kyc_doc_status }}
            </v-chip>
          </td>
          <td class="text-xs-center">
            <v-chip
              v-if="props.item.locked_user"
              :color="getStatusColor(props.item)"
              outline
              small
            >
              {{ props.item.locked_user }}
            </v-chip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              v-if="props.item.kyc_completed_at"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.kyc_completed_at) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.kyc_completed_at) }}
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            {{ props.item.mangopay_wallet_id }}
          </td>
          <td class="text-xs-center">
            {{ props.item.Balance ? centsToEuros(props.item.Balance.Amount) : 0 }}
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.created_at) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.created_at) }}
            </v-tooltip>
          </td>

          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="blue"
                  blue
                  flat
                  round
                  :loading="isCallingRestartKYc.includes(props.item.ext_user_id)"
                  @click.stop="onKYCSubmit(props.item.ext_user_id)"
                  v-on="on"
                >
                  <v-icon class="mr-2">
                    refresh
                  </v-icon> KYC reset
                </v-btn>
              </template>
              <span>Restart KYC process</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="blue"
                  flat
                  round
                  @click="openEditUser(props.item)"
                >
                  <v-icon class="mr-2">
                    edit
                  </v-icon> Edit
                </v-btn>
              </template>
              <span>edit</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
      <!-- USER ESCROWS TABLE START -->
      <template v-slot:expand="">
        <v-card class="expanded-details">
          <v-card-title class="toolbar white--text">
            Escrows
          </v-card-title>
          <v-data-table
            :headers="headersEscrows"
            :pagination.sync="paginationExpanded"
            :items="walletEscrows"
          >
            <template v-slot:items="props">
              <td class="text-xs-center">
                {{ props.item.id }}
              </td>
              <td class="text-xs-center">
                {{ props.item.source_wallet_id }}
              </td>
              <td class="text-xs-center">
                {{ props.item.target_wallet_id }}
              </td>
              <td class="text-xs-center">
                {{ props.item.state }}
              </td>
              <td class="text-xs-center">
                {{ props.item.reference }}
              </td>
              <td class="text-xs-center">
                {{ centsToEuros(props.item.amount) }}
              </td>
              <td class="text-xs-center">
                {{ formatDate(props.item.created_at) }}
              </td>
            </template>
          </v-data-table>
        </v-card>
      </template>
    </v-data-table>
    <v-dialog
      v-model="editModalOpen"
      width="1000"
      persistent
    >
      <v-card :loading="loading">
        <v-card-title
          class="headline success white--text"
          primary-title
        >
          <v-progress-linear
            v-if="loading"
            :indeterminate="true"
          />
          Update Wallet
        </v-card-title>
        <v-form
          ref="editEmoneyUserForm"
          @submit.prevent=""
        >
          <v-card-text>
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="formEditData.mangopay_user_id"
                  label="Mangopay User Id "
                  @input="$v.formEditData.mangopay_user_id.$touch()"
                  @blur="$v.formEditData.mangopay_user_id.$touch()"
                />
                <v-text-field
                  v-model="formEditData.mangopay_wallet_id"
                  label="Mangopay Wallet Id"
                  @input="$v.formEditData.mangopay_wallet_id.$touch()"
                  @blur="$v.formEditData.mangopay_wallet_id.$touch()"
                />
                <v-text-field
                  v-model="formEditData.wallet_iban"
                  label="Wallet Iban"
                  @input="$v.formEditData.wallet_iban.$touch()"
                  @blur="$v.formEditData.wallet_iban.$touch()"
                />
                <v-text-field
                  v-model="formEditData.wallet_bic"
                  label="Wallet Bic"
                  @input="$v.formEditData.wallet_bic.$touch()"
                  @blur="$v.formEditData.wallet_bic.$touch()"
                />
              </v-flex>
            </v-layout>
            <v-flex
              v-if="emoneyErrorOnUserUpdated"
              xs12
              class="error--text"
            >
              {{ emoneyErrorOnUserUpdated }}
            </v-flex>
          </v-card-text>
          <v-divider />

          <v-card-actions>
            <v-btn
              flat
              @click="editModalOpen = false"
            >
              Close
            </v-btn>
            <v-spacer />
            <v-btn
              class="primary"
              flat
              :disabled="$v.formEditData.$invalid || isLoading"
              :loading="isLoading"
              @click="handleSubmitUpdateWallet"
            >
              Update
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import KycStatusEnum from '@/enum/emoney/KycStatus';
import { toQueryString, setQuery } from '@/helpers';
import UserChip from '@/components/shared/user/UserChip';

export default {
  name: 'EmoneyUsersTable',
  components: {
    UserChip,
  },
  props: {
    emoneyUsers: {
      type: Array,
      default: () => [],
    },
    emoneyUsersPagination: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expand: false,
      expanded: [],
      approveModalOpen: false,
      cancelModalOpen: false,
      isLoadTableData: false,
      editModalOpen: false,
      formEditData: {},
      searchColumn: 'ext_user_id',
      search: '',
      isLoading: false,
      selected: [],
      searchColumnOptions: [
        { title: 'Exporo User ID', value: 'ext_user_id' },
        { title: 'MangoPay User ID', value: 'mangopay_user_id' },
        { title: 'MangoPay Wallet ID', value: 'mangopay_wallet_id' },
        { title: 'KYC Doc ID', value: 'kyc_doc_id' },
        { title: 'KYC Status', value: 'kyc_doc_status' },
        { title: 'Locked User Pattern', value: 'locked_at' },
      ],
      pagination: {
        page: this.emoneyUsersPagination.currentPage,
        rowsPerPage: this.emoneyUsersPagination.perPage,
        searchColumn: this.searchColumn || 'ext_user_id',
        sortBy: 'created_at',
        descending: true,
      },
      paginationExpanded: {
        // page: 1,
        rowsPerPage: 5,
        totalItems: 0,
        sortBy: 'created_at',
        descending: true,
      },
      rowsPerPageItems: [
        {
          text: '5',
          value: 5,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },

        {
          text: '100',
          value: 100,
        },
      ],
      filters: {
        createdAt: [],
      },
      resultFilters: {},
      headers: [
        {
          text: 'User',
          align: 'center',
          value: 'ext_user_id',
          sortable: true,
          width: '100px',
        },

        {
          text: 'Mangopay User ID',
          align: 'center',
          value: 'mangopay_user_id',
          sortable: true,
          width: '100px',
        },

        {
          text: 'KYC doc Id',
          align: 'center',
          value: 'kyc_doc_id',
          sortable: false,
        },
        {
          text: 'KYC status',
          align: 'center',
          value: 'kyc_doc_status',
          sortable: false,
        },
        {
          text: 'Locked User Pattern',
          align: 'center',
          value: 'locked_at',
          sortable: false,
        },
        {
          text: 'KYC created at',
          align: 'center',
          value: 'kyc_created_at',
          sortable: false,
        },
        {
          text: 'Wallet Id',
          align: 'center',
          value: 'mangopay_wallet_id',
          sortable: true,
        },
        {
          text: 'Balance',
          align: 'center',
          value: 'Balance.Amount',
          sortable: false,
        },
        {
          text: 'Created at',
          value: 'created_at',
          sortable: true,
        },
        {
          text: 'Action',
          align: 'center',
          sortable: false,
        },
      ],
      headersEscrows: [
        {
          text: 'ID',
          align: 'center',
          value: 'id',
        },
        {
          text: 'Source Wallet Id',
          align: 'center',
          value: 'source_wallet_id',
        },
        {
          text: 'Target Wallet Id',
          align: 'center',
          value: 'target_wallet_id',
        },
        {
          text: 'State',
          align: 'center',
          value: 'state',
        },
        {
          text: 'Reference',
          align: 'center',
          value: 'reference',
        },
        {
          text: 'Amount',
          align: 'center',
          value: 'amount',
        },
        {
          text: 'Created at',
          align: 'center',
          value: 'created_at',
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      'emoneySelectedUser',
      'emoneyErrorOnUserUpdated',
    ]),
    ...mapState({
      walletEscrows: state => state.emoneyUsersStore.walletEscrows,
      isCallingRestartKYc: state => state.emoneyUsersStore.isCallingRestartKYc,
      lockedUsers: state => state.emoneyUsersStore.lockedUsers,
    }),
    filteredItems() {
      return this.emoneyUsers.filter(d => Object.keys(this.filters).every((f) => {
        if (this.filters[f]) {
          if (f === 'locked_user') {
            return (
              this.filters[f] === 'true' ? d.locked_at : !d.locked_at
            );
          }

          return (
            this.filters[f].length < 1 ||
            String(this.filters[f]).includes(d[f])
          );
        }

        return true;
      }));
    },
    filteredResults() {
      if (this.hasResultFilters) {
        return this.filteredItems.filter((item) => {
          let requiredFields = 0;
          let found = 0;
          const { kycStatus, searchResults } = this.resultFilters;

          if (kycStatus) {
            requiredFields += 1;
            found += item.kyc_doc_status ? item.kyc_doc_status.includes(kycStatus) ? 1 : 0 : 0;
          }

          if (searchResults) {
            requiredFields += 1;
            found += Object.keys(item)
              .find(key => String(item[key]).toLowerCase()
                .includes(searchResults.toLowerCase())) ? 1 : 0;
          }

          return found === requiredFields;
        });
      }

      return this.filteredItems;
    },
    kycStatusEnum() {
      return KycStatusEnum;
    },
    kycStatus() {
      return Object.keys(this.kycStatusEnum)
        .map(status => this.kycStatusEnum[status]);
    },
    hasResultFilters() {
      return !!this.resultFilters.kycStatus
        || !!this.resultFilters.searchResults;
    },
  },
  watch: {
    async emoneyUsers() {
      this.getDefaultFilters();
      await this.$nextTick();
      await this.changePage();
    },
    emoneySelectedUser(user) {
      this.formEditData = user;
    },
    searchColumn: {
      immediate: false,
      handler(value) {
        setQuery(
          {
            searchColumn: value,
          },
          this.$route,
          this.$router,
        );
        this.pagination.searchColumn = value;
      },
    },
    search: {
      immediate: false,
      handler(value) {
        setQuery(
          {
            search: value,
          },
          this.$route,
          this.$router,
        );
        if (this.searchColumn === 'locked_user' && value === '20') {
          this.emoneyUsers = this.emoneyUsers.filter(user => user.locked_at && user.locked_at.startWith('20'));
        } else {
          this.onChangePagination();
        }
      },
    },
    emoneyUsersPagination: {
      immediate: false,
      async handler(emoneyUsersPagination) {
        if (emoneyUsersPagination) {
          this.pagination.page = emoneyUsersPagination.currentPage;
          this.pagination.rowsPerPage = emoneyUsersPagination.perPage;
          this.pagination.totalItems = emoneyUsersPagination.total;
        }
      },
    },
    async pagination() {
      this.onChangePagination();
    },
  },
  validations() {
    return {
      formEditData: {
        mangopay_user_id: { required },
        mangopay_wallet_id: { required },
        wallet_iban: { required },
        wallet_bic: { required },
      },
    };
  },
  methods: {
    ...mapActions(
      [
        'getEmoneyUsers',
        'getWalletEscrows',
        'resetUserKYC',
        'lockUser',
        'getLockedUsers',
        'setEmoneySelectedUser',
        'updateEmoneyUser',
      ],
    ),
    formatDate(date) {
      return this.$moment(date).format('MMMM Do YYYY, h:mm:ss a');
    },
    formatDateSmall(date) {
      return this.$moment(date).format('DD/MM/YYYY');
    },
    async expandRow(props) {
      this.getWalletEscrows(props.item.mangopay_wallet_id);
    },
    centsToEuros(amount) {
      amount = `${parseInt(amount, 0) / 100}`;

      return `${parseFloat(amount).toFixed(2)} €`;
    },
    onChangePagination() {
      const query = toQueryString({
        per_page: this.pagination.rowsPerPage,
        page: this.pagination.page,
        searchQuery: this.search,
        searchColumn: this.searchColumn,
        sortBy: this.pagination.sortBy,
        sortDirection: this.pagination.descending ? 'DESC' : 'ASC',
      });
      if (typeof window.LIT !== 'undefined') {
        clearTimeout(window.LIT);
      }
      window.LIT = setTimeout(() => {
        this.getEmoneyUsers(query);
      }, 1000);
    },
    toggleAll() {
      if (this.selected.length) {
        this.selected = [];
      } else {
        this.selected = this.emoneyUsers.slice();
      }
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
      this.onChangePagination();
    },
    updatePagination(pagination) {
      if (this.isLoadTableData) {
        setQuery(
          {
            page: pagination.page,
          },
          this.$route,
          this.$router,
        );
      }
    },
    toogleSelected(props) {
      props.selected = !props.selected;
    },

    getKycStatus(item) {
      const result = item.kyc_doc_status ? this.kycStatus
        .find(({ status }) => item.kyc_doc_status.indexOf(status) > -1)
        : null;

      return result || null;
    },

    getStatusColor(item) {
      const status = this.getKycStatus(item);

      return status ? status.color : 'secondary';
    },

    async changePage() {
      const { page, search, searchColumn } = this.$route.query;
      this.search = search;
      this.searchColumn = searchColumn;
      await this.$nextTick();
      this.isLoadTableData = true;
      this.pagination.page = page ? Number(page) : 1;
    },
    getDefaultFilters() {
      if (this.$route.query) {
        Object.keys(this.$route.query).forEach((item) => {
          let value = this.$route.query[item];

          if (Object.prototype.hasOwnProperty.call(this.filters, item)) {
            if (typeof this.filters[item] === 'string') {
              this.filters[item] = value;
            } else if (String(this.$route.query[item]).includes(';')) {
              value = this.$route.query[item].split(';');
              this.filters[item] = value;
            } else {
              this.filters[item].push(value);
            }
          }
        });
      }
    },
    resetResultsFilters() {
      this.resultFilters = _.mapValues(this.resultFilters, () => null);
    },
    async approveTrades() {
      const data = { ids: this.selected.map(v => v.id) };
      await this.approveEmoneyUsers(data);
      this.approveModalOpen = false;
      this.selected = [];
    },
    async cancelTrades() {
      const data = { ids: this.selected.map(v => v.id) };

      await this.cancelEmoneyUsers(data);
      this.cancelModalOpen = false;
      this.selected = [];
    },
    async restartMangoPayKYC(userId) {
      await this.resetUserKYC(userId);
    },
    openEditUser(props) {
      this.setEmoneySelectedUser(props);
      this.editModalOpen = true;
    },
    async lockUserAccount(data) {
      await this.lockUser(data);
    },
    async onKYCSubmit(userId) {
      await this.restartMangoPayKYC(userId);
      await this.onChangePagination();
    },
    async handleSubmitUpdateWallet() {
      this.$v.$touch();

      if (!this.$v.formEditData.$invalid) {
        this.isLoading = true;
        try {
          await this.updateEmoneyUser(this.formEditData);
          this.editModalOpen = false;
          this.formEditData = {};
          this.$v.$reset();
        } catch (err) {
          console.info(err);
        }
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
a {
  text-decoration: none;
  color: black;
}

.toolbar {
  background-color: #1a4c7d;
  color: white;
}

.infoLabel {
  margin-left: 16px;
}

.countLabel {
  color: #fff;
}
.expanded-details {
  background-color: #eaeaea;
  padding: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/emoney/EmoneyUsersTable.vue